import React from 'react';

import SesamTextField from 'Common/SesamTextField/SesamTextField';
import CollapsePanel from 'Common/CollapsePanel/CollapsePanel';
import MultiSelector from '../multi-selector';
import Button from 'Common/Button/Button';
import { Form, FormActions } from 'Common/forms';

import { TestID } from '../../testID';

import type { DnsRule, RuleValidationError } from './DnsForwardingEditor.types';

import './DnsForwardingEditor.scss';

export const DnsForwardingEditor: React.FC<{
  rules: DnsRule[];
  editErrors: Record<number, RuleValidationError>;
  newDomain: string;
  newIps: string[];
  addError: RuleValidationError | null;
  isSubmitting: boolean;
  onNewDomainChange: (value: string) => void;
  onNewIpsChange: (ips: string[]) => void;
  onAddRule: () => void;
  onEditDomain: (index: number, value: string) => void;
  onEditIps: (index: number, ips: string[]) => void;
  onSaveRule: (index: number) => void;
  onDeleteRule: (index: number) => void;
}> = ({
  rules,
  editErrors,
  newDomain,
  newIps,
  addError,
  isSubmitting,
  onNewDomainChange,
  onNewIpsChange,
  onAddRule,
  onEditDomain,
  onEditIps,
  onSaveRule,
  onDeleteRule,
}) => (
  <div className="dns-forwarding-editor">
    <div className="row">
      <div className="col gr-secondary">
        <h2 className="heading-section">Add a DNS forwarding rule</h2>
        <div className="material-form">
          <Form
            onSubmit={(ev) => {
              ev.preventDefault();
            }}
          >
            <SesamTextField
              margin="normal"
              name="domain"
              type="text"
              InputProps={{ placeholder: 'e.g. internal.example.com' }}
              value={newDomain}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onNewDomainChange(e.target.value)
              }
              label="Domain"
              autoComplete="off"
              error={!!addError?.domain}
              required={true}
              data-testid={TestID.DnsForwardingDomainInput}
            />
            {addError?.domain && <div className="dns-validation-error">{addError.domain}</div>}

            <MultiSelector
              name="dns_servers"
              label="DNS Servers"
              placeholder="Enter DNS server IPs separated by space"
              onChange={(e: { target: { name: string; value: string[] } }) =>
                onNewIpsChange(e.target.value)
              }
              items={newIps}
              disabledOptions={true}
              error={!!addError?.ips}
              required={true}
            />
            {addError?.ips && <div className="dns-validation-error">{addError.ips}</div>}

            <FormActions>
              <Button
                text={isSubmitting ? 'Loading...' : 'Add rule'}
                color="primary"
                onClick={onAddRule}
                disabled={isSubmitting}
                data-testid={TestID.DnsForwardingAddRuleButton}
              />
            </FormActions>
          </Form>
        </div>
      </div>

      <div className="col gr-primary">
        <h2 className="heading-section">Existing DNS rules</h2>
        {rules.length === 0 && (
          <p className="dns-empty-message">No DNS forwarding rules configured.</p>
        )}
        {rules.map((rule, index) => (
          <CollapsePanel
            title={rule.domain || `Rule ${index + 1}`}
            startCollapsed={false}
            key={rule.id}
          >
            <div className="material-form">
              <Form
                onSubmit={(ev) => {
                  ev.preventDefault();
                }}
              >
                <SesamTextField
                  margin="normal"
                  name="domain"
                  type="text"
                  InputProps={{ placeholder: 'e.g. internal.example.com' }}
                  value={rule.domain}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onEditDomain(index, e.target.value)
                  }
                  label="Domain"
                  autoComplete="off"
                  error={!!editErrors[index]?.domain}
                  required={true}
                  data-testid={TestID.DnsForwardingDomainInput}
                />
                {editErrors[index]?.domain && (
                  <div className="dns-validation-error">{editErrors[index].domain}</div>
                )}

                <MultiSelector
                  name="dns_servers"
                  label="DNS Servers"
                  placeholder="Enter DNS server IPs separated by space"
                  onChange={(e: { target: { name: string; value: string[] } }) =>
                    onEditIps(index, e.target.value)
                  }
                  items={rule.ips}
                  disabledOptions={true}
                  error={!!editErrors[index]?.ips}
                  required={true}
                />
                {editErrors[index]?.ips && (
                  <div className="dns-validation-error">{editErrors[index].ips}</div>
                )}

                <FormActions>
                  <Button
                    text={isSubmitting ? 'Loading...' : 'Save'}
                    color="primary"
                    onClick={() => onSaveRule(index)}
                    disabled={isSubmitting}
                    data-testid={TestID.DnsForwardingSaveButton}
                  />
                  <Button
                    text={isSubmitting ? 'Loading...' : 'Delete'}
                    theme="danger"
                    onClick={() => onDeleteRule(index)}
                    disabled={isSubmitting}
                    data-testid={TestID.DnsForwardingRemoveRuleButton}
                  />
                </FormActions>
              </Form>
            </div>
          </CollapsePanel>
        ))}
      </div>
    </div>

    <p className="dns-info-message">Changes will take effect after the next provisioning cycle.</p>
  </div>
);
